import type { StatusTone } from '@vnetwork/ui/data';
import type { ChipTone } from '@vnetwork/ui/primitives';

/**
 * Ticket status → `StatusPill` tone and priority → `Chip` tone maps for the
 * customer SLA & Tickets tab. Reproduces the legacy ManageSLA option coloring
 * on the @ui tone palette (opening=info, in-progress=warning, done=success,
 * cancelled=danger). `humanize` (status-tones.ts) handles the label text.
 */

/** Ticket status → StatusPill tone. */
export function ticketStatusTone(status?: string): StatusTone {
  switch (status) {
    case 'completed':
    case 'closed':
      return 'success';
    case 'work-in-progress':
      return 'warning';
    case 'opening':
      return 'info';
    case 'cancelled':
      return 'danger';
    case 'pending':
    default:
      return 'neutral';
  }
}

/** Ticket priority → Chip tone. high=danger, medium=warning, low=neutral. */
export function ticketPriorityTone(priority?: string): ChipTone {
  switch (priority) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
    default:
      return 'neutral';
  }
}

/**
 * Ticket type → Chip tone, keyed off the (already Title Cased) type label so the
 * type reads as a colored category: create=success, error/cancel=danger,
 * upgrade/change=warning, poc/trial=info, else primary.
 */
export function ticketTypeTone(type?: string): ChipTone {
  const t = (type ?? '').toLowerCase();
  if (!t) return 'neutral';
  if (/error|cancel|terminat|delet|fail|problem/.test(t)) return 'danger';
  if (/upgrade|change|update|migrat/.test(t)) return 'warning';
  if (/poc|trial/.test(t)) return 'info';
  if (/create|new|activ/.test(t)) return 'success';
  return 'primary';
}
